/**
 * Filas de tarjetas del builder que se CENTRAN cuando sobran columnas.
 *
 * El problema: una sección con `lg:grid-cols-3` y solo 2 tarjetas deja la
 * tercera columna vacía, así que el bloque queda pegado a la izquierda con un
 * hueco grande a la derecha. Pasa en "Nuestro equipo", estadísticas, logos,
 * testimonios, etc. — cualquier grilla cuyo contenido lo pone el usuario.
 *
 * La solución: `flex flex-wrap justify-center` en el contenedor y un ancho
 * porcentual por tarjeta idéntico al que tendría en la grilla. Así las
 * tarjetas conservan su tamaño de diseño y el espacio sobrante se reparte a
 * ambos lados en vez de acumularse a la derecha (y la última fila incompleta
 * también queda centrada).
 *
 * El gap se lee de la variable CSS `--bg-gap`, que el contenedor debe declarar
 * junto al `gap` real. Se hace así para que el gap pueda ser responsive
 * (`gap-6 md:gap-8 [--bg-gap:1.5rem] md:[--bg-gap:2rem]`) o dinámico en px
 * (`style={{ gap, ['--bg-gap']: gap }}`), sin que el helper tenga que saberlo.
 *
 * Las clases están escritas COMPLETAS a propósito: Tailwind purga por
 * coincidencia de texto y no ve clases armadas por interpolación.
 */

export type AutoFitBp = 'base' | 'sm' | 'md' | 'lg' | 'xl';

/** Ancho de tarjeta por cantidad de columnas: 100%/n menos su parte del gap. */
const W: Record<AutoFitBp, Record<number, string>> = {
  base: {
    1: 'w-full',
    2: 'w-[calc(50%_-_var(--bg-gap)/2)]',
    3: 'w-[calc(33.333%_-_var(--bg-gap)*2/3)]',
    4: 'w-[calc(25%_-_var(--bg-gap)*3/4)]',
    5: 'w-[calc(20%_-_var(--bg-gap)*4/5)]',
    6: 'w-[calc(16.666%_-_var(--bg-gap)*5/6)]',
  },
  sm: {
    1: 'sm:w-full',
    2: 'sm:w-[calc(50%_-_var(--bg-gap)/2)]',
    3: 'sm:w-[calc(33.333%_-_var(--bg-gap)*2/3)]',
    4: 'sm:w-[calc(25%_-_var(--bg-gap)*3/4)]',
    5: 'sm:w-[calc(20%_-_var(--bg-gap)*4/5)]',
    6: 'sm:w-[calc(16.666%_-_var(--bg-gap)*5/6)]',
  },
  md: {
    1: 'md:w-full',
    2: 'md:w-[calc(50%_-_var(--bg-gap)/2)]',
    3: 'md:w-[calc(33.333%_-_var(--bg-gap)*2/3)]',
    4: 'md:w-[calc(25%_-_var(--bg-gap)*3/4)]',
    5: 'md:w-[calc(20%_-_var(--bg-gap)*4/5)]',
    6: 'md:w-[calc(16.666%_-_var(--bg-gap)*5/6)]',
  },
  lg: {
    1: 'lg:w-full',
    2: 'lg:w-[calc(50%_-_var(--bg-gap)/2)]',
    3: 'lg:w-[calc(33.333%_-_var(--bg-gap)*2/3)]',
    4: 'lg:w-[calc(25%_-_var(--bg-gap)*3/4)]',
    5: 'lg:w-[calc(20%_-_var(--bg-gap)*4/5)]',
    6: 'lg:w-[calc(16.666%_-_var(--bg-gap)*5/6)]',
  },
  xl: {
    1: 'xl:w-full',
    2: 'xl:w-[calc(50%_-_var(--bg-gap)/2)]',
    3: 'xl:w-[calc(33.333%_-_var(--bg-gap)*2/3)]',
    4: 'xl:w-[calc(25%_-_var(--bg-gap)*3/4)]',
    5: 'xl:w-[calc(20%_-_var(--bg-gap)*4/5)]',
    6: 'xl:w-[calc(16.666%_-_var(--bg-gap)*5/6)]',
  },
};

/** Clase del contenedor. El gap y `--bg-gap` los pone cada sección. */
export const AUTO_FIT_ROW = 'flex flex-wrap justify-center';

export interface AutoFitStep {
  /** Breakpoint desde el que aplica. Omitido = mobile-first. */
  bp?: AutoFitBp;
  /** Columnas a esa altura (1 a 6). */
  cols: number;
}

const clampCols = (n: number) => Math.min(6, Math.max(1, Math.round(n) || 1));

/**
 * Clase de cada tarjeta para una escalera de breakpoints.
 *
 * autoFitItem([{ cols: 1 }, { bp: 'sm', cols: 2 }, { bp: 'lg', cols: 3 }])
 *   → 1 columna en móvil, 2 en sm, 3 en lg — centradas cuando sobren.
 */
export const autoFitItem = (steps: AutoFitStep[]): string =>
  ['min-w-0', ...steps.map(({ bp = 'base', cols }) => W[bp][clampCols(cols)])].join(' ');

/**
 * Escalera estándar de las secciones del builder: 1 columna en móvil, 2 en el
 * breakpoint intermedio y `columns` en lg. Con 1 o 2 columnas pedidas no hay
 * escalón de más.
 */
export const autoFitItemForColumns = (columns: number, bp: 'sm' | 'md' = 'sm'): string => {
  const cols = clampCols(columns);
  if (cols === 1) return autoFitItem([{ cols: 1 }]);
  if (cols === 2) return autoFitItem([{ cols: 1 }, { bp, cols: 2 }]);
  return autoFitItem([{ cols: 1 }, { bp, cols: 2 }, { bp: 'lg', cols }]);
};

/**
 * Normaliza el prop `columns`. Los selects del panel de settings guardan
 * STRINGS ('3'), así que comparar con `=== 3` fallaba y la sección caía al
 * caso por defecto (casi siempre 4 columnas) apenas el usuario tocaba el
 * selector.
 */
export const normalizeColumns = (value: unknown, fallback: number): number => {
  const n = typeof value === 'number' ? value : parseInt(String(value ?? ''), 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};
